import countries from 'i18n-iso-countries'
import enLocale from 'i18n-iso-countries/langs/en.json'
import type {
  ProductSelection,
  Q4BBrokerUse,
  Q4IInvestUse,
  Q4XWealthUse,
  Q5Frequency,
  Q6AnnualVolume,
  Q7SourceOfFunds,
  Q8FundsOwnership,
  Q9Occupation,
  Q10PepRca,
} from '../types/coreApi-types'

// Single source of truth for the risk-assessment questionnaire labels, shared by
// the customer-facing ComplianceQuestionnaireModal and the admin/MLRO answer
// editor. Mirrors the customer-facing copy in
// apps/api/src/Penning/Infrastructure/Services/Compliance/RuleSets/crm-001-v7.9.yaml
// (questions section). When MLRO updates copy in YAML, sync these labels too.

export type QuestionnaireOption<T extends string> = { value: T; label: string }

// v7.9: two products (as in v7.7). The v7.8 "Wealth" product was removed — the
// Veli-migration risk is now a derived cohort contribution, not a product the
// customer picks (see crm-001-v7.9.yaml veliMigrationContribution).
export const Q0_OPTIONS: QuestionnaireOption<ProductSelection>[] = [
  { value: 'Broker', label: 'Broker — buy, sell, trade and convert crypto' },
  {
    value: 'Invest',
    label: 'Invest — Penning manages a crypto portfolio for me',
  },
]

export const Q4B_OPTIONS: QuestionnaireOption<Q4BBrokerUse>[] = [
  { value: 'BuyHold', label: 'Buy and hold crypto as an investment' },
  {
    value: 'ActiveTrade',
    label: 'Actively trade crypto (buy and sell regularly)',
  },
  { value: 'SellToFiat', label: 'Sell crypto and receive fiat (cash out to bank)' },
  { value: 'Convert', label: 'Convert between different crypto assets' },
  { value: 'Payments', label: 'Receive or send crypto payments' },
]

export const Q4I_OPTIONS: QuestionnaireOption<Q4IInvestUse>[] = [
  {
    value: 'FiatInFiatOut',
    label: 'Fund by fiat bank transfer, exit by fiat bank transfer',
  },
  {
    value: 'CryptoInFiatOut',
    label: 'Fund by crypto transfer, exit by fiat bank transfer',
  },
  {
    value: 'FiatInCryptoOut',
    label: 'Fund by fiat bank transfer, exit by crypto transfer',
  },
  {
    value: 'CryptoInCryptoOut',
    label: 'Fund by crypto transfer AND exit by crypto transfer',
  },
]

// Legacy — the Wealth product and its Q4-X index-strategy question were removed
// in CRM-001 v7.9 (the Veli-migration risk is now a derived cohort contribution,
// not a product). Kept only so historical (v7.8) stored answers can still be
// labelled if displayed.
export const Q4X_OPTIONS: QuestionnaireOption<Q4XWealthUse>[] = [
  { value: 'Stable', label: 'Stable (e.g. Stable BTC, Stable 4 Coins)' },
  { value: 'CryptoTop10', label: 'Crypto Top 10 (flagship)' },
  { value: 'Balanced', label: 'Balanced (e.g. Balanced ETH, Balanced Top 5)' },
  { value: 'Moderate', label: 'Moderate (e.g. Moderate Top 10, Moderate Momentum)' },
]

export const Q5_OPTIONS: QuestionnaireOption<Q5Frequency>[] = [
  { label: 'Rarely — up to 12 times per year', value: 'Rarely' },
  { label: 'Monthly — 12 to 24 times per year', value: 'Monthly' },
  { label: 'Weekly — 25 to 104 times per year', value: 'Weekly' },
  { label: 'Daily — 105 or more times per year', value: 'Daily' },
]

export const Q6_OPTIONS: QuestionnaireOption<Q6AnnualVolume>[] = [
  { label: 'Under €10,000 per year', value: 'Under10k' },
  { label: '€10,001 – €50,000 per year', value: 'Between10kAnd50k' },
  { label: 'More than €50,000 per year', value: 'Above50k' },
]

export const Q7_OPTIONS: QuestionnaireOption<Q7SourceOfFunds>[] = [
  { value: 'Employment', label: 'Employment income (salary or wages)' },
  { value: 'Pension', label: 'Pension or retirement income' },
  { value: 'SelfEmploy', label: 'Self-employment or freelance income' },
  { value: 'InvestmentReturns', label: 'Investment or trading returns (fiat)' },
  { value: 'Savings', label: 'Savings accumulated over time' },
  { value: 'Inheritance', label: 'Inheritance or gift' },
  {
    value: 'PropertyOrAssetSale',
    label: 'Proceeds from sale of property or assets',
  },
  {
    value: 'CryptoSoldElsewhere',
    label: 'Proceeds from sale of crypto assets held elsewhere',
  },
  { value: 'MiningOrStaking', label: 'Crypto mining or staking rewards' },
  {
    value: 'CryptoSalary',
    label: 'Crypto received as salary or payment for services',
  },
]

export const Q8_OPTIONS: QuestionnaireOption<Q8FundsOwnership>[] = [
  { label: 'Yes — these are entirely my own funds', value: 'OwnFunds' },
  {
    label: 'No — managing or investing on behalf of someone else',
    value: 'ThirdParty',
  },
]

export const Q9_OPTIONS: QuestionnaireOption<Q9Occupation>[] = [
  { label: 'Employed', value: 'Employed' },
  { label: 'Self-employed or business owner', value: 'SelfEmployed' },
  { label: 'Retired', value: 'Retired' },
  { label: 'Student', value: 'Student' },
  { label: 'Unemployed or not currently working', value: 'Unemployed' },
]

export const Q10_OPTIONS: QuestionnaireOption<Q10PepRca>[] = [
  { label: 'No', value: 'No' },
  {
    label:
      'Yes — I or a close contact holds or recently held a prominent public position',
    value: 'Yes',
  },
]

// Nationality is not a scored question — it is a declared risk-model data point,
// compared against the document-derived nationality from KYC. ISO 3166-1 alpha-2
// codes with English country names, sorted alphabetically by name.
countries.registerLocale(enLocale)

export const NATIONALITY_OPTIONS: QuestionnaireOption<string>[] = Object.entries(
  countries.getNames('en', { select: 'official' }),
)
  .map(([value, label]) => ({ value, label }))
  .sort((a, b) => a.label.localeCompare(b.label))

// The same ISO 3166-1 alpha-2 country list serves the pre-KYC country-of-residence selector
// (DeclareKycResidence). Distinct name so the residence gate reads as residence, not nationality.
export const COUNTRY_OF_RESIDENCE_OPTIONS: QuestionnaireOption<string>[] =
  NATIONALITY_OPTIONS
